import React, { useCallback, useEffect, useRef, useState } from 'react'
import SydButton from '../../../../../../../commonDesign/Button'
import { smartEndpoints } from '../../../../../../../../api/smartEndpoints'
import { useSmartEndpointForm } from '../../SmartEndpointFormContext'
import { useSmartEndpointWorkspace } from '../../SmartEndpointWorkspaceContext'
import { DETAIL_TABS } from './testFixtures'
import { buildTestRequest, mapTestResponse } from './requestUtils'
import { useStyles } from './styles'
import TestList from './TestList'
import TestConfig from './TestConfig'
import TestOutput from './TestOutput'
import RequestDetailModal from './RequestDetailModal'

function TestsPanel () {
  const classes = useStyles()
  const form = useSmartEndpointForm()
  const workspace = useSmartEndpointWorkspace()

  const [selectedIndex, setSelectedIndex] = useState(null)
  const [tests, setTests] = useState([])
  const [runMenuOpen, setRunMenuOpen] = useState(false)
  const [activeDetailTab, setActiveDetailTab] = useState('config')
  const [activeOutputTab, setActiveOutputTab] = useState('result')
  const [inspectedRequest, setInspectedRequest] = useState(null)
  const [runningTests, setRunningTests] = useState(new Set())
  const menuRef = useRef(null)

  const executeQuery = smartEndpoints.useExecuteQuery()
  const executeCommand = smartEndpoints.useExecuteCommand()

  const passedCount = tests.filter(t => t.status === 'passed').length
  const failedCount = tests.filter(t => t.status === 'failed').length
  const selectedTest = selectedIndex !== null ? tests[selectedIndex] : null

  const runSingleTest = useCallback(async (index) => {
    const test = tests[index]
    if (!test || !form.source) return

    setRunningTests(prev => new Set([...prev, index]))

    const updateTestResult = (result) => {
      setTests(prev => prev.map((t, i) => i === index ? { ...t, ...result } : t))
      if (selectedIndex === index) {
        setActiveDetailTab('output')
        setActiveOutputTab('result')
      }
    }

    try {
      const request = buildTestRequest(test, form.source, workspace.endpointConfig)
      const effectiveMode = test.modeOverride || workspace.mode
      const mutation = effectiveMode === 'command' ? executeCommand : executeQuery
      const response = await mutation.mutateAsync(request)
      const hasAssertions = !!request.assertion
      const result = mapTestResponse(response, { hasAssertions })

      updateTestResult({
        status: result.pass ? 'passed' : 'failed',
        lastRunMs: result.executionMs,
        lastResult: result
      })
    } catch (err) {
      updateTestResult({
        status: 'failed',
        lastRunMs: 0,
        lastResult: {
          pass: false,
          executionMs: 0,
          result: null,
          error: { message: err?.message || 'Request failed', name: 'NetworkError' },
          assertionError: null,
          requests: [],
          logs: []
        }
      })
    } finally {
      setRunningTests(prev => {
        const next = new Set(prev)
        next.delete(index)
        return next
      })
    }
  }, [tests, form.source, workspace.mode, workspace.endpointConfig, executeQuery, executeCommand, selectedIndex])

  const runTestsByFilter = useCallback(async (filterFn) => {
    const indices = tests.map((t, i) => filterFn(t) ? i : -1).filter(i => i >= 0)
    for (const index of indices) {
      await runSingleTest(index)
    }
  }, [tests, runSingleTest])

  const handleSelectTest = useCallback((index) => {
    if (selectedIndex === index) {
      setSelectedIndex(null)
    } else {
      setSelectedIndex(index)
      setInspectedRequest(null)
      const test = tests[index]
      setActiveDetailTab(test.lastResult ? 'output' : 'config')
      if (test.lastResult) setActiveOutputTab('result')
    }
  }, [selectedIndex, tests])

  useEffect(() => {
    if (!runMenuOpen) return
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setRunMenuOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [runMenuOpen])

  const applyTestProposal = useCallback((proposal) => {
    if (proposal.remove) {
      setTests(prev => prev.filter(t => t.name !== proposal.name))
      setSelectedIndex(null)
      return
    }

    setTests(prev => {
      const existingIdx = prev.findIndex(t => t.name === proposal.name)
      const testEntry = {
        name: proposal.name,
        testMode: proposal.mocks?.strategy === 'throwOnMiss' ? 'unit' : 'integration',
        status: null,
        lastRunMs: null,
        input: JSON.stringify(proposal.variables || {}, null, 2),
        responses: JSON.stringify(proposal.mocks?.responses || [], null, 2),
        assertions: proposal.assertion || '',
        lastResult: null
      }
      if (existingIdx >= 0) {
        const updated = [...prev]
        updated[existingIdx] = { ...updated[existingIdx], ...testEntry, status: null, lastResult: null }
        return updated
      }
      return [...prev, testEntry]
    })
  }, [])

  useEffect(() => {
    return workspace.registerAction('applyTest', applyTestProposal)
  }, [workspace, applyTestProposal])

  const loadTests = useCallback((loadedTests) => {
    setTests(loadedTests)
    setSelectedIndex(null)
  }, [])

  useEffect(() => {
    return workspace.registerAction('loadTests', loadTests)
  }, [workspace, loadTests])

  useEffect(() => {
    if (workspace.testsRef) workspace.testsRef.current = tests
    workspace.bumpTestsVersion()
  }, [tests]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddTest = useCallback(() => {
    const defaultInput = workspace.mode === 'query'
      ? JSON.stringify({ filters: {}, dates: {} }, null, 2)
      : '{}'

    setTests(prev => [...prev, {
      name: 'Untitled test',
      testMode: 'unit',
      status: null,
      lastRunMs: null,
      input: defaultInput,
      responses: '[]',
      assertions: '',
      lastResult: null
    }])
    setSelectedIndex(tests.length)
    setActiveDetailTab('config')
    setInspectedRequest(null)
  }, [tests.length, workspace.mode])

  const handleRemoveTest = useCallback((index) => {
    setTests(prev => prev.filter((_, i) => i !== index))
    if (selectedIndex === index) setSelectedIndex(null)
    else if (selectedIndex !== null && selectedIndex > index) setSelectedIndex(selectedIndex - 1)
  }, [selectedIndex])

  const handleTestConfigChange = useCallback((index, field, value) => {
    setTests(prev => prev.map((t, i) => i === index ? { ...t, [field]: value } : t))
  }, [])

  const isAnyRunning = runningTests.size > 0

  return (
    <div className={classes.container}>
      <div className={classes.toolbar}>
        <span className={classes.summary}>
          {tests.length} tests &middot; {passedCount} passed{failedCount > 0 ? ` \u00b7 ${failedCount} failed` : ''}
        </span>
        <div className={classes.toolbarActions}>
          <SydButton size='xs' variant='outline' icon='add' onClick={handleAddTest}>Add Test</SydButton>
          <div className={classes.runMenuWrapper} ref={menuRef}>
            <SydButton
              size='xs'
              variant='primary'
              onClick={() => setRunMenuOpen(!runMenuOpen)}
              disabled={isAnyRunning || !form.source}
            >
              {isAnyRunning ? 'Running\u2026' : 'Run Tests \u25BE'}
            </SydButton>
            {runMenuOpen && (
              <div className={classes.runMenu}>
                <button className={classes.menuItem} onClick={() => { setRunMenuOpen(false); runTestsByFilter(t => t.testMode === 'unit') }}>
                  Run Unit Tests
                </button>
                <button className={classes.menuItem} onClick={() => { setRunMenuOpen(false); runTestsByFilter(t => t.testMode === 'integration') }}>
                  Run Integration Tests
                </button>
                <div className={classes.menuDivider} />
                <button className={classes.menuItem} onClick={() => { setRunMenuOpen(false); runTestsByFilter(() => true) }}>
                  <strong>Run All Tests</strong>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <TestList
        tests={tests}
        selectedIndex={selectedIndex}
        runningTests={runningTests}
        onSelect={handleSelectTest}
        onRun={runSingleTest}
        onRemove={handleRemoveTest}
        classes={classes}
      />

      {!selectedTest && (
        <div className={classes.detailEmpty}>
          Select a test to view its configuration and output
        </div>
      )}

      {selectedTest && (
        <div className={classes.detailArea}>
          <div className={classes.detailHeader}>
            <span className={classes.detailTitle}>{selectedTest.name}</span>
            {selectedTest.lastResult && (
              <span
                style={{
                  fontSize: 'var(--text-size-xs)',
                  fontWeight: 600,
                  color: selectedTest.lastResult.pass ? 'var(--color-success-600)' : 'var(--color-danger-600)'
                }}
              >
                {selectedTest.lastResult.pass ? '\u2713 Passed' : '\u2717 Failed'} &mdash; {selectedTest.lastResult.executionMs}ms
              </span>
            )}
          </div>

          <div className={classes.detailTabs}>
            {DETAIL_TABS.map(tab => (
              <div
                key={tab.id}
                className={`${classes.detailTab} ${activeDetailTab === tab.id ? classes.detailTabActive : ''}`}
                onClick={() => setActiveDetailTab(tab.id)}
              >
                {tab.label}
                {tab.id === 'output' && selectedTest.lastResult && !selectedTest.lastResult.pass && (
                  <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', backgroundColor: 'var(--color-danger-500)', marginLeft: 5, verticalAlign: 'middle' }} />
                )}
              </div>
            ))}
          </div>

          <div className={classes.detailContent}>
            {activeDetailTab === 'config' && (
              <TestConfig
                test={selectedTest}
                selectedIndex={selectedIndex}
                onConfigChange={handleTestConfigChange}
                classes={classes}
              />
            )}

            {activeDetailTab === 'output' && (
              <TestOutput
                lastResult={selectedTest.lastResult}
                activeOutputTab={activeOutputTab}
                setActiveOutputTab={setActiveOutputTab}
                onInspectRequest={setInspectedRequest}
                classes={classes}
              />
            )}
          </div>
        </div>
      )}

      <RequestDetailModal
        request={inspectedRequest}
        classes={classes}
        onClose={() => setInspectedRequest(null)}
      />
    </div>
  )
}

export default TestsPanel
